<template>
  <div>
    <!-- LOCKED -->
    <div v-if="isLocked" class="tw-flex tw-items-center tw-justify-center" style="min-height: 60vh;" data-testid="passcode-gate">
      <div class="tw-text-center tw-w-full" style="max-width: 380px;">
        <div class="tw-mb-6">
          <i class="icon-lock tw-text-6xl tw-text-gray-400" />
        </div>

        <h3 class="tw-font-bold tw-mb-2">{{ $t('pages.passcode.gate.title') }}</h3>
        <p class="text-muted tw-mb-6">{{ $t('pages.passcode.gate.subtitle') }}</p>

        <b-form @submit.prevent="onSubmit" class="tw-text-left">
          <b-form-group>
            <b-form-input
              ref="passcodeInput"
              v-model="passcode"
              data-testid="passcode-gate-input"
              type="password"
              size="lg"
              :placeholder="$t('pages.passcode.modal.placeholder')"
              :state="error ? false : null"
              autofocus
            />
            <b-form-invalid-feedback v-if="error" data-testid="passcode-gate-error">
              {{ errorMessage }}
            </b-form-invalid-feedback>
          </b-form-group>

          <b-button
            type="submit"
            variant="primary"
            block
            size="lg"
            :disabled="loading || !passcode"
            class="tw-mt-2"
            data-testid="passcode-gate-submit"
          >
            <b-spinner v-if="loading" small class="mr-2" />
            {{ $t('pages.passcode.modal.submit') }}
          </b-button>
        </b-form>

        <div class="tw-mt-4">
          <small class="text-muted">
            {{ $t('pages.passcode.modal.forgot') }}
            <router-link :to="{ name: 'passcode-settings' }">
              {{ $t('pages.passcode.gate.goToSettings') }}
            </router-link>
          </small>
        </div>

        <div v-if="sessionExpired" class="tw-mt-4">
          <b-badge variant="light" class="tw-px-3 tw-py-2 tw-text-xs tw-text-gray-500">
            <i class="icon-clock tw-mr-1" />
            {{ $t('pages.passcode.gate.sessionExpired') }}
          </b-badge>
        </div>
      </div>
    </div>

    <!-- UNLOCKED -->
    <template v-else>
      <b-card v-if="hasPasscode" class="mb-3" body-class="tw-py-1 tw-px-3">
        <div class="tw-flex tw-items-center tw-justify-between">
          <div class="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-600">
            <i class="icon-lock" />
            <span>{{ $t('pages.passcode.session.protectedSection') }}</span>
            <span class="tw-font-mono tw-font-semibold tw-text-gray-800">{{ remainingFormatted }}</span>
            <span class="tw-text-gray-400">{{ $t('pages.passcode.session.remaining') }}</span>
          </div>
          <button
            class="tw-text-sm tw-text-red-500 hover:tw-text-red-700 tw-font-medium tw-bg-transparent tw-border-0 tw-cursor-pointer"
            @click="lockNow"
          >
            <i class="icon-lock-open tw-mr-1" />
            {{ $t('pages.passcode.session.lockNow') }}
          </button>
        </div>
      </b-card>

      <slot />
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'PasscodeGate',

  data() {
    return {
      passcode: '',
      error: false,
      errorMessage: '',
      loading: false,
      sessionExpired: false,
      remainingSeconds: 0,
    };
  },

  created() {
    this._expiryTimer = null;
    this._countdownInterval = null;
  },

  computed: {
    ...mapGetters('auth', ['hasPasscode', 'validPasscodeToken']),

    isLocked() {
      return this.hasPasscode && !this.validPasscodeToken;
    },

    remainingFormatted() {
      const m = Math.floor(this.remainingSeconds / 60);
      const s = this.remainingSeconds % 60;
      return `${m}:${s < 10 ? '0' : ''}${s}`;
    },
  },

  watch: {
    validPasscodeToken: {
      immediate: true,
      handler(token) {
        this._clearTimers();
        if (token) {
          this.sessionExpired = false;
          this._startTracking(token);
        } else {
          this.remainingSeconds = 0;
        }
      },
    },

    isLocked(locked) {
      if (locked) this.sessionExpired = true;
      this.$emit('locked', locked);
    },
  },

  beforeDestroy() {
    this._clearTimers();
  },

  methods: {
    async onSubmit() {
      this.error = false;
      this.loading = true;

      try {
        await this.$store.dispatch('auth/verifyPasscode', { passcode: this.passcode });
        this.passcode = '';
        this.sessionExpired = false;
      } catch (e) {
        const code = e.response?.data?.code;
        if (code === 'UNAUTHORIZED') {
          this.errorMessage = this.$t('pages.passcode.modal.incorrect');
        } else {
          this.errorMessage = e.response?.data?.message || this.$t('errors.text');
        }
        this.error = true;
        this.passcode = '';
      } finally {
        this.loading = false;
      }
    },

    lockNow() {
      this.$store.dispatch('auth/clearPasscodeToken');
    },

    _startTracking(token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiresAt = payload.exp * 1000;
        const msUntilExpiry = expiresAt - Date.now();

        if (msUntilExpiry <= 0) {
          this.$store.dispatch('auth/clearPasscodeToken');
          return;
        }

        this.remainingSeconds = Math.ceil(msUntilExpiry / 1000);

        this._countdownInterval = setInterval(() => {
          this.remainingSeconds = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
        }, 1000);

        this._expiryTimer = setTimeout(() => {
          this.$store.dispatch('auth/clearPasscodeToken');
        }, msUntilExpiry);
      } catch (e) {
        this.$store.dispatch('auth/clearPasscodeToken');
      }
    },

    _clearTimers() {
      if (this._expiryTimer) {
        clearTimeout(this._expiryTimer);
        this._expiryTimer = null;
      }
      if (this._countdownInterval) {
        clearInterval(this._countdownInterval);
        this._countdownInterval = null;
      }
    },
  },
};
</script>
